import type { Project } from "@szyos/contracts";
import {
  MAX_DONE_CRITERIA,
  MAX_FOCUS_PROJECTS,
  MIN_RECOMMENDED_DONE_CRITERIA,
  doneProgress,
  type NextStep,
} from "@szyos/domain";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useId, useState, type FormEvent } from "react";
import { api, errorMessage } from "../api";
import { ErrorNote, useToast } from "./feedback";
import { Icon } from "../icons";

/** Fertigstellen vor Anfangen (UX-10): Bausteine für Fokus, „Fertig heißt“ und den nächsten Schritt. */

/** Genau ein nächster Schritt; Aufträge führen zum Auftrag, alles andere zur Projektseite. */
export function NextStepLink({
  step,
  projectId,
  onCriterion,
  project,
  compact = false,
}: {
  step: NextStep;
  projectId: string;
  /** Auf der Projektseite: Kriterium als Titel für einen neuen Auftrag übernehmen. */
  onCriterion?: (text: string) => void;
  /** Auf der Projektseite: „in den Fokus nehmen“ wirkt direkt, mit Auswahl zum Tauschen bei vollem Fokus. */
  project?: Project;
  /** In der Projektliste: kurzer Link ohne Vorspann statt Knopf. */
  compact?: boolean;
}) {
  const prefix = compact ? null : (
    <>
      <span className="muted">Nächster Schritt:</span>{" "}
    </>
  );
  const strong = compact ? "link" : "btn btn--primary btn--small";
  if (step.kind === "none") return <span className="next-step muted">{step.label}</span>;
  if (step.kind === "focus" && project) {
    return (
      <span className="next-step">
        {prefix}
        <FocusControl project={project} label={step.label} primary />
      </span>
    );
  }
  if (step.kind === "order" || step.kind === "wait") {
    return (
      <span className="next-step">
        {prefix}
        <Link
          to="/work-orders/$orderId"
          params={{ orderId: step.orderId }}
          className={step.kind === "order" ? strong : "link"}
        >
          {step.label}
        </Link>{" "}
        <span className="muted">bei „{step.orderTitle}“</span>
      </span>
    );
  }
  if (step.kind === "criterion" && onCriterion) {
    return (
      <span className="next-step">
        {prefix}
        <button type="button" className={strong} onClick={() => onCriterion(step.criterion)}>
          {step.label} „{step.criterion}“
        </button>
      </span>
    );
  }
  return (
    <span className="next-step">
      {prefix}
      <Link
        to="/projects/$projectId"
        params={{ projectId }}
        hash={step.kind === "focus" ? "fokus" : "fertig"}
        className={strong}
      >
        {step.kind === "criterion" ? `${step.label} „${step.criterion}“` : step.label}
      </Link>
    </span>
  );
}

/** Kurzform für Karten: „2 von 5 erfüllt“, ohne Prozent (Gestaltungsregel 1). */
export function DoneBadge({ project }: { project: Project }) {
  const { met, total } = doneProgress(project.doneCriteria);
  if (total === 0) return null;
  return (
    <span className="chip chip--ghost">
      <Icon name="check" /> {met} von {total} erfüllt
    </span>
  );
}

/** Auswahl, welches Fokusprojekt pausiert, wenn der Fokus voll ist. */
export function PauseChooser({
  focused,
  value,
  onChange,
}: {
  focused: Project[];
  value: string;
  onChange: (id: string) => void;
}) {
  const name = useId();
  return (
    <fieldset className="pause-chooser">
      <legend>Im Fokus sind schon {MAX_FOCUS_PROJECTS} Projekte. Welches pausiert dafür?</legend>
      {focused.map((p) => (
        <label key={p.id} className="pause-option">
          <input
            type="radio"
            name={name}
            value={p.id}
            checked={value === p.id}
            onChange={() => onChange(p.id)}
          />{" "}
          {p.title}
        </label>
      ))}
    </fieldset>
  );
}

/** In den Fokus nehmen oder herausnehmen; bei vollem Fokus erst wählen, was pausiert. */
export function FocusControl({
  project,
  label = "In den Fokus",
  primary = false,
}: {
  project: Project;
  label?: string;
  /** Als Hauptknopf (nächster Schritt) statt als kleiner Chip. */
  primary?: boolean;
}) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const projects = useQuery({ queryKey: ["projects"], queryFn: api.projects });
  const [choosing, setChoosing] = useState(false);
  const [pause, setPause] = useState("");
  const focused = (projects.data ?? []).filter((p) => p.focus && p.id !== project.id);
  const full = focused.length >= MAX_FOCUS_PROJECTS;
  const save = useMutation({
    mutationFn: (input: { focus: boolean; pauseProjectId?: string }) =>
      api.setProjectFocus(project.id, input.focus, input.pauseProjectId),
    onSuccess: async (p) => {
      queryClient.setQueryData(["project", project.id], p);
      await queryClient.invalidateQueries({ queryKey: ["projects"] });
      setChoosing(false);
      setPause("");
      toast(p.focus ? `„${p.title}“ ist im Fokus` : `„${p.title}“ ist geparkt`);
    },
    onError: (err) => toast(errorMessage(err), "error"),
  });
  if (project.lifecycle === "closed") return null;
  if (project.focus) {
    return (
      <button
        type="button"
        className="chip-btn is-primary"
        aria-pressed="true"
        disabled={save.isPending}
        onClick={() => save.mutate({ focus: false })}
      >
        <Icon name="target" /> Im Fokus
      </button>
    );
  }
  if (!choosing) {
    return (
      <button
        type="button"
        className={primary ? "btn btn--primary btn--small" : "chip-btn"}
        aria-pressed={primary ? undefined : "false"}
        disabled={save.isPending || projects.isLoading}
        onClick={() => (full ? setChoosing(true) : save.mutate({ focus: true }))}
      >
        <Icon name="target" /> {label}
      </button>
    );
  }
  return (
    <span className="focus-choose panel">
      <PauseChooser focused={focused} value={pause} onChange={setPause} />
      <span className="idea-actions">
        <button
          type="button"
          className="btn btn--primary"
          disabled={!pause || save.isPending}
          onClick={() => save.mutate({ focus: true, pauseProjectId: pause })}
        >
          <span className="btn-label">Tauschen</span>
        </button>
        <button type="button" className="btn" onClick={() => setChoosing(false)}>
          <span className="btn-label">Abbrechen</span>
        </button>
      </span>
    </span>
  );
}

/** „Fertig heißt“ und „Später“ pflegen; jede Änderung wird sofort gespeichert. */
export function DonePanel({ project: saved }: { project: Project }) {
  const queryClient = useQueryClient();
  const ids = { criterion: useId(), later: useId() };
  const [criterion, setCriterion] = useState("");
  const [later, setLater] = useState("");
  const [error, setError] = useState<string | null>(null);
  // Ausstehende Änderung sofort zeigen; gesteuerte Checkboxen sprängen sonst bis zur Antwort zurück.
  const [draft, setDraft] = useState<{ criteria: { text: string; met: boolean }[]; later: string[] } | null>(
    null,
  );
  const project: Project = draft
    ? {
        ...saved,
        doneCriteria: draft.criteria.map((c) => ({
          ...c,
          metAt: c.met ? (saved.doneCriteria.find((x) => x.text === c.text)?.metAt ?? null) : null,
        })),
        later: draft.later,
      }
    : saved;
  const criteria = project.doneCriteria.map(({ text, met }) => ({ text, met }));
  const save = useMutation({
    mutationFn: (input: { criteria: { text: string; met: boolean }[]; later: string[] }) =>
      api.setProjectDone(project.id, input),
    onSuccess: async (p) => {
      setError(null);
      queryClient.setQueryData(["project", project.id], p);
      await queryClient.invalidateQueries({ queryKey: ["projects"] });
    },
    onError: (err) => setError(errorMessage(err)),
    onSettled: () => setDraft(null),
  });
  const store = (next: { criteria?: typeof criteria; later?: string[] }) => {
    const input = { criteria: next.criteria ?? criteria, later: next.later ?? project.later };
    setDraft(input);
    save.mutate(input);
  };
  const { met, total } = doneProgress(project.doneCriteria);

  const addCriterion = (e: FormEvent) => {
    e.preventDefault();
    const text = criterion.trim();
    if (!text) return setError("Kriterium eingeben.");
    if (total >= MAX_DONE_CRITERIA) {
      return setError(`Höchstens ${MAX_DONE_CRITERIA} Kriterien. Weiteres gehört zu „Später“.`);
    }
    setCriterion("");
    store({ criteria: [...criteria, { text, met: false }] });
  };
  const addLater = (e: FormEvent) => {
    e.preventDefault();
    const text = later.trim();
    if (!text) return setError("Eintrag für „Später“ eingeben.");
    setLater("");
    store({ later: [...project.later, text] });
  };

  return (
    <section className="panel done-panel" id="fertig" aria-labelledby="doneHead">
      <h2 id="doneHead">Fertig heißt</h2>
      <p className="muted" role="status">
        {total === 0
          ? `Lege ${MIN_RECOMMENDED_DONE_CRITERIA} bis ${MAX_DONE_CRITERIA} prüfbare Kriterien für die erste fertige Version fest.`
          : `${met} von ${total} erfüllt.${total < MIN_RECOMMENDED_DONE_CRITERIA ? ` Empfohlen sind mindestens ${MIN_RECOMMENDED_DONE_CRITERIA}.` : ""}`}
      </p>
      {total > 0 && (
        <ul className="done-list">
          {project.doneCriteria.map((c, i) => (
            <li key={c.text} className={c.met ? "is-met" : undefined}>
              <label>
                <input
                  type="checkbox"
                  checked={c.met}
                  disabled={save.isPending}
                  onChange={() => {
                    // Erfüllt heißt belegt; die Rückfrage verhindert Haken aus Versehen (UX-Umbau).
                    if (
                      !c.met &&
                      !window.confirm(`Erfüllt: „${c.text}“? Setze den Haken nur, wenn du es gesehen hast.`)
                    )
                      return;
                    store({ criteria: criteria.map((x, j) => (j === i ? { ...x, met: !x.met } : x)) });
                  }}
                />{" "}
                {c.text}
              </label>
              <span className="done-actions">
                <button
                  type="button"
                  className="btn btn--ghost btn--small"
                  disabled={save.isPending}
                  aria-label={`Später: „${c.text}“ verschieben`}
                  onClick={() =>
                    store({ criteria: criteria.filter((_, j) => j !== i), later: [...project.later, c.text] })
                  }
                >
                  Später
                </button>
                <button
                  type="button"
                  className="btn btn--ghost btn--small"
                  disabled={save.isPending}
                  aria-label={`„${c.text}“ entfernen`}
                  onClick={() => store({ criteria: criteria.filter((_, j) => j !== i) })}
                >
                  <Icon name="trash" />
                </button>
              </span>
            </li>
          ))}
        </ul>
      )}
      <form className="inline-form" onSubmit={addCriterion} noValidate>
        <label htmlFor={ids.criterion}>Neues Kriterium</label>
        <span className="inline-row">
          <input
            id={ids.criterion}
            value={criterion}
            onChange={(e) => setCriterion(e.target.value)}
            maxLength={300}
            placeholder="z. B. Läuft offline auf dem Handy"
          />
          <button type="submit" className="btn" disabled={save.isPending}>
            <span className="btn-label">
              <Icon name="plus" /> Hinzufügen
            </span>
          </button>
        </span>
      </form>
      <h3>Später</h3>
      <p className="muted">
        Alles, was nicht zur ersten fertigen Version gehört. Es bleibt hier, statt den Auftrag aufzublähen.
      </p>
      {project.later.length > 0 && (
        <ul className="done-list">
          {project.later.map((text, i) => (
            <li key={`${i}-${text}`}>
              <span>{text}</span>
              <span className="done-actions">
                <button
                  type="button"
                  className="btn btn--ghost btn--small"
                  disabled={save.isPending || total >= MAX_DONE_CRITERIA}
                  aria-label={`Jetzt: „${text}“ zu „Fertig heißt“ holen`}
                  onClick={() =>
                    store({
                      criteria: [...criteria, { text, met: false }],
                      later: project.later.filter((_, j) => j !== i),
                    })
                  }
                >
                  Jetzt
                </button>
                <button
                  type="button"
                  className="btn btn--ghost btn--small"
                  disabled={save.isPending}
                  aria-label={`„${text}“ entfernen`}
                  onClick={() => store({ later: project.later.filter((_, j) => j !== i) })}
                >
                  <Icon name="trash" />
                </button>
              </span>
            </li>
          ))}
        </ul>
      )}
      <form className="inline-form" onSubmit={addLater} noValidate>
        <label htmlFor={ids.later}>Für später notieren</label>
        <span className="inline-row">
          <input id={ids.later} value={later} onChange={(e) => setLater(e.target.value)} maxLength={300} />
          <button type="submit" className="btn" disabled={save.isPending}>
            <span className="btn-label">
              <Icon name="plus" /> Notieren
            </span>
          </button>
        </span>
      </form>
      <ErrorNote error={error} />
    </section>
  );
}
